import React from 'react';

import {
  Box,
  SimpleGrid,
  Text,
  Icon,
  Collapse,
  Badge,
  HStack,
} from '@chakra-ui/react';
import { FaChartLine, FaFileCsv, FaSlidersH } from 'react-icons/fa';

import { useColors } from './colors';

const TABS = [
  {
    id: 'progress',
    label: 'Run progress',
    description: 'Step-by-step run status and overall progress',
    icon: FaChartLine,
  },
  {
    id: 'mapping',
    label: 'Supplier SKU → Partslink mapping',
    description: 'Upload the two-column CSV used to resolve supplier SKUs',
    icon: FaFileCsv,
  },
  {
    id: 'fields',
    label: 'Listing fields configuration',
    description: 'Initial listing values and rules for compiling catalog data',
    icon: FaSlidersH,
  },
];

const SettingsSectionCard = ({
  activePanel,
  onPanelChange,
  progressSummary,
  progressBadgeColorScheme = 'teal',
  mappingReady,
  children,
}) => {
  const { textColor, backgroundCardColor, borderColor, secondaryTextColor, mutedBg } = useColors();

  const handleTabClick = (id) => {
    onPanelChange(activePanel === id ? null : id);
  };

  return (
    <Box>
      <Box
        p={4}
        bg={backgroundCardColor}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={borderColor}
        boxShadow="sm"
      >
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
          {TABS.map((tab) => {
            const isActive = activePanel === tab.id;
            return (
              <Box
                key={tab.id}
                as="button"
                type="button"
                onClick={() => handleTabClick(tab.id)}
                p={4}
                textAlign="left"
                borderRadius="md"
                borderWidth="2px"
                borderColor={isActive ? 'teal.400' : borderColor}
                bg={isActive ? mutedBg : backgroundCardColor}
                transition="all 0.2s"
                _hover={{
                  borderColor: 'teal.400',
                  transform: 'translateY(-1px)',
                  boxShadow: 'sm',
                }}
                cursor="pointer"
                w="full"
              >
                <HStack spacing={3} align="flex-start" mb={2}>
                  <Icon
                    as={tab.icon}
                    boxSize={5}
                    color={isActive ? 'teal.500' : secondaryTextColor}
                    mt={0.5}
                  />
                  <Box flex={1}>
                    <Text color={textColor} fontSize="sm" fontWeight="semibold" lineHeight="short">
                      {tab.label}
                    </Text>
                  </Box>
                </HStack>
                <Text color={secondaryTextColor} fontSize="xs" lineHeight="short" pl={8}>
                  {tab.description}
                </Text>
                {tab.id === 'progress' && progressSummary && (
                  <Badge
                    mt={2}
                    ml={8}
                    colorScheme={progressBadgeColorScheme}
                    variant="subtle"
                    fontSize="xs"
                  >
                    {progressSummary}
                  </Badge>
                )}
                {tab.id === 'mapping' && mappingReady && (
                  <Badge mt={2} ml={8} colorScheme="green" variant="subtle" fontSize="xs">
                    File attached
                  </Badge>
                )}
              </Box>
            );
          })}
        </SimpleGrid>
      </Box>

      <Collapse in={Boolean(activePanel)} animateOpacity>
        <Box
          mt={4}
          p={{ base: 4, md: 5 }}
          bg={backgroundCardColor}
          borderRadius="lg"
          borderWidth="1px"
          borderColor={borderColor}
          boxShadow="sm"
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

export default SettingsSectionCard;
